import React, { useState, useEffect } from 'react';
import { ShieldAlert, Bell, RefreshCw, CheckCircle, ArrowUpCircle, ArrowDownCircle } from 'lucide-react';
import api from '../services/api';

// Estilo por categoria de evento del panel
const CAT = {
  robo:        { c: '#dc2626', bg: '#fee2e2', label: 'Robo' },
  fuego:       { c: '#dc2626', bg: '#fee2e2', label: 'Fuego' },
  panico:      { c: '#dc2626', bg: '#fee2e2', label: 'Pánico' },
  medica:      { c: '#db2777', bg: '#fce7f3', label: 'Médica' },
  sabotaje:    { c: '#ea580c', bg: '#ffedd5', label: 'Sabotaje' },
  armado:      { c: '#059669', bg: '#d1fae5', label: 'Armado' },
  sistema:     { c: '#ca8a04', bg: '#fef3c7', label: 'Sistema' },
  test:        { c: '#6b7280', bg: '#f3f4f6', label: 'Test' },
  desconocido: { c: '#6b7280', bg: '#f3f4f6', label: 'Otro' },
};
const catOf = (k) => CAT[k] || CAT.desconocido;
const fmt = (t) => (t ? new Date(t).toLocaleString('es-MX') : '—');

const PanelAlarmas = () => {
  const [tab, setTab] = useState('alarmas');   // alarmas | eventos
  const [alarms, setAlarms] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [a, e] = await Promise.all([
        api.get('/api/panel/alarms'),
        api.get('/api/panel/events?limit=200'),
      ]);
      setAlarms(a.data || []);
      setEvents(e.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const abiertas = alarms.filter((a) => a.estado === 'abierta');

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><ShieldAlert size={26} /> Panel de Alarma</h1>
          <p style={S.subtitle}>Eventos recibidos del AX PRO (SIA DC-09 / Contact ID)</p>
        </div>
        <button style={S.refreshBtn} onClick={load}>
          <RefreshCw size={16} className={loading ? 'spin' : ''} /> Actualizar
        </button>
      </header>

      <div style={S.kpis}>
        <div style={{ ...S.kpi, borderLeftColor: '#dc2626' }}>
          <div style={S.kpiVal}>{abiertas.length}</div>
          <div style={S.kpiLab}>Alarmas abiertas</div>
        </div>
        <div style={{ ...S.kpi, borderLeftColor: '#ea580c' }}>
          <div style={S.kpiVal}>{alarms.length}</div>
          <div style={S.kpiLab}>Alarmas (total)</div>
        </div>
        <div style={{ ...S.kpi, borderLeftColor: '#3b82f6' }}>
          <div style={S.kpiVal}>{events.length}</div>
          <div style={S.kpiLab}>Eventos recientes</div>
        </div>
        <div style={{ ...S.kpi, borderLeftColor: '#059669' }}>
          <div style={S.kpiVal}>{events[0] ? fmt(events[0].received_at).split(' ')[1] || '—' : '—'}</div>
          <div style={S.kpiLab}>Última actividad</div>
        </div>
      </div>

      <div style={S.tabs}>
        <button onClick={() => setTab('alarmas')} style={{ ...S.tab, ...(tab === 'alarmas' ? S.tabActive : {}) }}>
          <ShieldAlert size={15} /> Alarmas
        </button>
        <button onClick={() => setTab('eventos')} style={{ ...S.tab, ...(tab === 'eventos' ? S.tabActive : {}) }}>
          <Bell size={15} /> Todos los eventos
        </button>
      </div>

      {tab === 'alarmas' && (
        <div style={S.list}>
          {alarms.length === 0 && (
            <div style={S.empty}><CheckCircle size={48} color="#10b981" /><p>Sin alarmas registradas</p></div>
          )}
          {alarms.map((a) => {
            const cat = catOf(a.tipo);
            return (
              <div key={`${a.id_origen}`} style={S.card}>
                <div style={{ ...S.bar, background: cat.c }} />
                <div style={S.cardContent}>
                  <div style={S.rowHead}>
                    <span style={{ ...S.badge, background: cat.bg, color: cat.c }}>{cat.label}</span>
                    <span style={S.acct}>Cuenta {a.device_id}</span>
                    {a.channel != null && <span style={S.zona}>Zona {a.channel}</span>}
                    <span style={S.time}>{fmt(a.triggered_at)}</span>
                  </div>
                  <p style={S.msg}>{a.mensaje}</p>
                  <div style={S.rowFoot}>
                    <span style={{ ...S.estado, color: a.estado === 'abierta' ? '#dc2626' : '#059669' }}>
                      {a.estado === 'abierta'
                        ? '● Abierta'
                        : `✓ Resuelta${a.resolved_at ? ' · ' + fmt(a.resolved_at) : ''}`}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {tab === 'eventos' && (
        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Hora</th>
                <th style={S.th}>Categoría</th>
                <th style={S.th}>Evento</th>
                <th style={S.th}>Cuenta</th>
                <th style={S.th}>Zona/Usuario</th>
                <th style={S.th}>Tipo</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && (
                <tr><td colSpan={6} style={S.tdEmpty}>Sin eventos aún</td></tr>
              )}
              {events.map((e) => {
                const cat = catOf(e.categoria);
                return (
                  <tr key={e.id} style={S.tr}>
                    <td style={S.td}>{fmt(e.received_at)}</td>
                    <td style={S.td}>
                      <span style={{ ...S.badge, background: cat.bg, color: cat.c }}>{cat.label}</span>
                    </td>
                    <td style={S.td}>{e.descripcion}</td>
                    <td style={S.td}>{e.account}</td>
                    <td style={S.td}>{e.zona_usuario && e.zona_usuario !== '000' ? e.zona_usuario : '—'}</td>
                    <td style={S.td}>
                      {e.qualifier === 1
                        ? <span style={S.qOn}><ArrowUpCircle size={13} /> Activación</span>
                        : e.qualifier === 3
                          ? <span style={S.qOff}><ArrowDownCircle size={13} /> Restauración</span>
                          : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const S = {
  container: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  refreshBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px',
                background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },

  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 },
  kpi: { background: 'white', padding: 14, borderRadius: 8, borderLeft: '4px solid', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  kpiVal: { fontSize: 22, fontWeight: 700 },
  kpiLab: { fontSize: 12, color: '#6b7280', marginTop: 4 },

  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white',
         cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 },
  tabActive: { background: '#3b82f6', color: 'white', borderColor: '#3b82f6' },

  list: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { background: 'white', borderRadius: 8, display: 'flex', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  bar: { width: 5 },
  cardContent: { flex: 1, padding: 14 },
  rowHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 },
  badge: { padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 700 },
  acct: { fontSize: 13, fontWeight: 600 },
  zona: { fontSize: 12, background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: 4 },
  time: { fontSize: 12, color: '#9ca3af', marginLeft: 'auto' },
  msg: { margin: '4px 0', color: '#374151', fontSize: 14 },
  rowFoot: { display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 },
  estado: { fontSize: 13, fontWeight: 600 },

  tableWrap: { background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 14px', background: '#f9fafb', color: '#6b7280',
        fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', color: '#374151' },
  tdEmpty: { padding: 40, textAlign: 'center', color: '#9ca3af' },
  qOn: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#dc2626' },
  qOff: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#059669' },

  empty: { textAlign: 'center', padding: 60, color: '#6b7280',
           display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 },
};

export default PanelAlarmas;
